import React from 'react';
import { useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { useTemplateStore } from 'Hooks/zustand/useTemplateStore';
import { getUser } from 'Selectors';
import { RQ_ALL_SIMULATIONS, CONFIRMED_FINISHED_SIMULATION } from 'Utils';
import { SIMULATION_STATUS } from 'Common/enums';
import useGetLastMinuteSimulations from './query-hooks/simulations/useGetLastMinuteSimulations';

type LocalSimulationObj = {
    [status: string]: {
        total: number;
        simulationIds: number[];
    };
};

const useIsSimulating = (
    enabled: boolean,
    onSuccess?: (repoId: number) => void,
    onError?: () => void,
    customDuration?: number
) => {
    const queryClient = useQueryClient();
    const { templateRepositoryId } = useTemplateStore.getState().template;
    const { userId } = useSelector(getUser);
    const shouldPoll = React.useRef(false);
    const isNewPoll = React.useRef(true);
    const alerts = React.useRef({
        alertSuccess: false,
        alertError: false
    });
    const finishedSimsimulationIds = localStorage.getItem(CONFIRMED_FINISHED_SIMULATION);
    const storeFinishedSimulationId = finishedSimsimulationIds ? JSON.parse(finishedSimsimulationIds) : [];
    const prevRecord = React.useRef({
        [SIMULATION_STATUS.DEPLOYING]: {
            total: 0,
            simulationIds: []
        },
        [SIMULATION_STATUS.QUEUED]: {
            total: 0,
            simulationIds: []
        },
        [SIMULATION_STATUS.PROCESSING]: {
            total: 0,
            simulationIds: []
        },
        [SIMULATION_STATUS.FINISHED]: {
            total: 0,
            simulationIds: storeFinishedSimulationId
        },
        [SIMULATION_STATUS.DEPLOYMENT_FAILED]: {
            total: 0,
            simulationIds: []
        },
        [SIMULATION_STATUS.FAILED]: {
            total: 0,
            simulationIds: []
        }
    } as LocalSimulationObj);

    const getIsNewPoll = React.useCallback(
        (
            deployingCount: number,
            queuedCount: number,
            processingCount: number,
            finishedCount: number,
            failedCount: number,
            o: LocalSimulationObj
        ) => {
            return (
                deployingCount !== o[SIMULATION_STATUS.DEPLOYING].total ||
                queuedCount !== o[SIMULATION_STATUS.QUEUED].total ||
                processingCount !== o[SIMULATION_STATUS.PROCESSING].total ||
                finishedCount !== o[SIMULATION_STATUS.FINISHED].total ||
                failedCount !== o[SIMULATION_STATUS.FAILED].total
            );
        },
        []
    );

    const lastMinuteSimulations = useGetLastMinuteSimulations(
        enabled,
        templateRepositoryId!,
        Boolean(shouldPoll.current),
        // onSuccess
        (shm: SimulationsHashmap) => {
            if (
                shm[userId] === undefined &&
                (prevRecord.current[SIMULATION_STATUS.DEPLOYING].total > 0 ||
                    prevRecord.current[SIMULATION_STATUS.PROCESSING].total > 0 ||
                    prevRecord.current[SIMULATION_STATUS.QUEUED].total > 0)
            ) {
                shouldPoll.current = true;
            } else if (shm[userId] === undefined) {
                shouldPoll.current = false;
            } else {
                // Hence simulation hashmap sets [status] : {summary} pairs dynamically from API, it won't
                // have simulations with status "DEPLOYING_SIMULATION_FILES" once they are moved into a queue
                // Thus, default values need to be set conditionally
                const deployingCount = shm[userId]?.[SIMULATION_STATUS.DEPLOYING]?.total ?? 0;
                const queuedCount = shm[userId]?.[SIMULATION_STATUS.QUEUED]?.total ?? 0;
                const processingCount = shm[userId]?.[SIMULATION_STATUS.PROCESSING]?.total ?? 0;
                const finishedCount = shm[userId]?.[SIMULATION_STATUS.FINISHED]?.total ?? 0;
                const failedDeploymentCount = shm[userId]?.[SIMULATION_STATUS.DEPLOYMENT_FAILED]?.total ?? 0;
                const failedCount = (shm[userId]?.[SIMULATION_STATUS.FAILED]?.total ?? 0) + failedDeploymentCount;

                const deployingSimulations = shm[userId]?.[SIMULATION_STATUS.DEPLOYING]?.simulationIds ?? [];
                const queuedSimulations = shm[userId]?.[SIMULATION_STATUS.QUEUED]?.simulationIds ?? [];
                const processingSimulations = shm[userId]?.[SIMULATION_STATUS.PROCESSING]?.simulationIds ?? [];
                const finishedSimulations = shm[userId]?.[SIMULATION_STATUS.FINISHED]?.simulationIds ?? [];
                const failedDeploymentSimulations =
                    shm[userId]?.[SIMULATION_STATUS.DEPLOYMENT_FAILED]?.simulationIds ?? [];
                const failedSimulations = shm[userId]?.[SIMULATION_STATUS.FAILED]?.simulationIds ?? [];

                // when a new response is received, determine whether to fire alerts or not
                alerts.current = {
                    alertError: failedCount !== prevRecord.current[SIMULATION_STATUS.FAILED].total && failedCount > 0,
                    alertSuccess:
                        !finishedSimulations.every((elem) =>
                            prevRecord.current[SIMULATION_STATUS.FINISHED].simulationIds.includes(elem)
                        ) && finishedCount > 0
                };

                isNewPoll.current = getIsNewPoll(
                    deployingCount,
                    queuedCount,
                    processingCount,
                    finishedCount,
                    failedCount,
                    prevRecord.current
                );

                // A new API response was returned with some newly finished simulations
                if (finishedCount > 0 && finishedCount !== prevRecord.current[SIMULATION_STATUS.FINISHED].total) {
                    if (alerts.current.alertSuccess) {
                        onSuccess?.(templateRepositoryId!);
                        alerts.current = { ...alerts.current, alertSuccess: false };
                        localStorage.setItem(CONFIRMED_FINISHED_SIMULATION, JSON.stringify(finishedSimulations));
                    }
                }

                // A new API response was returned with some newly failed simulations
                if (
                    failedCount > 0 &&
                    failedCount !==
                        prevRecord.current[SIMULATION_STATUS.FAILED].total +
                            prevRecord.current[SIMULATION_STATUS.DEPLOYMENT_FAILED].total
                ) {
                    if (alerts.current.alertError) {
                        onError?.();
                        alerts.current = { ...alerts.current, alertError: false };
                    }
                }

                // This block is intentionally placed here
                // We need localStates to compare against subsequent api repsonses to know
                // which simulation has changed its status and update UI correspondingly
                // NOTE: on first poll, localStates !== api repsonses
                if (isNewPoll) {
                    prevRecord.current = {
                        [SIMULATION_STATUS.DEPLOYING]: {
                            total: deployingCount,
                            simulationIds: deployingSimulations
                        },
                        [SIMULATION_STATUS.QUEUED]: {
                            total: queuedCount,
                            simulationIds: queuedSimulations
                        },
                        [SIMULATION_STATUS.PROCESSING]: {
                            total: processingCount,
                            simulationIds: processingSimulations
                        },
                        [SIMULATION_STATUS.FINISHED]: {
                            total: finishedCount,
                            simulationIds: finishedSimulations
                        },
                        [SIMULATION_STATUS.DEPLOYMENT_FAILED]: {
                            total: failedDeploymentCount,
                            simulationIds: failedDeploymentSimulations
                        },
                        [SIMULATION_STATUS.FAILED]: {
                            total: failedCount,
                            simulationIds: failedSimulations
                        }
                    };
                }

                // There're still simulations in queue
                if (deployingCount > 0 || queuedCount > 0 || processingCount > 0) {
                    shouldPoll.current = true;
                }

                // All simulations are done
                if (
                    !isNewPoll.current &&
                    deployingCount === 0 &&
                    queuedCount === 0 &&
                    prevRecord.current[SIMULATION_STATUS.QUEUED].total === 0 &&
                    processingCount === 0 &&
                    finishedCount === prevRecord.current[SIMULATION_STATUS.FINISHED].total
                ) {
                    shouldPoll.current = false;
                }
            }

            // update the table as long as responses are returned so that users see most up-to-date table data
            queryClient.invalidateQueries([RQ_ALL_SIMULATIONS]);
        },
        // onError
        (err: any) => {
            if (shouldPoll) {
                shouldPoll.current = false;
            }
            if (err && (err as any).code !== 403 && (err as any).code !== 401) {
                onError?.();
            }
        },
        customDuration
    );

    return { shouldPoll, lastMinuteSimulations };
};

export default useIsSimulating;
